/**
 * Per-shift answers (SPEC §1.1F) — the only spelling of what a volunteer's
 * answer to one shift is, whether it still holds, and what it makes of the
 * person as a whole.
 *
 * An answer is to a shift *as it stood*: its day, post, time and meeting point
 * (`ShiftFacts`). A breaking change to the shift leaves the answer behind, so
 * that shift — and only that shift — is asked again. A row can carry several
 * variants of the facts: one for the schedule the volunteer's page shows and
 * one for the coordinator's working state, which differ while a change is
 * unpublished.
 */
import type { ConfirmationStatus } from './constants';
import { minutesToHm } from './time';

export const SHIFT_ANSWER_STATUSES = ['ok', 'change', 'cant', 'pending'] as const;
export type ShiftAnswerStatus = (typeof SHIFT_ANSWER_STATUSES)[number];
/** What a volunteer can say about a shift from their page. */
export type ShiftAnswerGiven = Exclude<ShiftAnswerStatus, 'pending'>;

export interface ShiftFacts {
  shiftId: string;
  dayKey: string;
  /** `''` for a pinned shift whose post no longer resolves. */
  postId: string;
  postCode: string;
  postMicro: string;
  fromMin: number;
  toMin: number;
  meetMin: number | null;
  meetPlace: string;
}

export interface ShiftAnswerRow {
  id: string;
  shiftId: string;
  status: ShiftAnswerStatus;
  comment: string;
  facts: readonly ShiftFacts[];
  createdAt: Date | string;
  actorType?: 'volunteer' | 'coordinator' | 'system';
  carried?: boolean;
}

export function toFacts(r: {
  id: string;
  dayKey: string;
  postId: string;
  postCode: string;
  postMicro: string;
  fromMin: number;
  toMin: number;
  meetMin: number | null;
  meetPlace: string;
}): ShiftFacts {
  return {
    shiftId: r.id,
    dayKey: r.dayKey,
    postId: r.postId,
    postCode: r.postCode,
    postMicro: r.postMicro,
    fromMin: r.fromMin,
    toMin: r.toMin,
    meetMin: r.meetMin,
    meetPlace: r.meetPlace,
  };
}

/**
 * The same shift as far as an agreement goes. The post compares by id wherever
 * both sides know it, as the change diff does, so a corrected micro-location
 * does not ask anybody again.
 */
export function sameFacts(a: ShiftFacts, b: ShiftFacts): boolean {
  const samePost = a.postId && b.postId ? a.postId === b.postId : a.postCode === b.postCode && a.postMicro === b.postMicro;
  return (
    a.shiftId === b.shiftId &&
    a.dayKey === b.dayKey &&
    samePost &&
    a.fromMin === b.fromMin &&
    a.toMin === b.toMin &&
    a.meetMin === b.meetMin &&
    a.meetPlace === b.meetPlace
  );
}

const time = (d: Date | string) => new Date(d).getTime();

/** The newest row for a shift, whatever it says. */
export function latestAnswerRow<R extends ShiftAnswerRow>(rows: readonly R[], shiftId: string): R | null {
  let latest: R | null = null;
  for (const r of rows) {
    if (r.shiftId !== shiftId) continue;
    if (!latest || time(r.createdAt) > time(latest.createdAt) || (time(r.createdAt) === time(latest.createdAt) && r.id > latest.id)) {
      latest = r;
    }
  }
  return latest;
}

/**
 * The answer that stands for a shift as it is described by `f`: the newest row
 * for the shift, if it was given for these facts. Not the newest row that
 * matches — a refusal given after an agreement replaces it, and moving the
 * shift back must not bring the agreement back.
 */
export function answerFor<R extends ShiftAnswerRow>(rows: readonly R[], f: ShiftFacts): R | null {
  const latest = latestAnswerRow(rows, f.shiftId);
  if (!latest || latest.status === 'pending') return null;
  return latest.facts.some((x) => sameFacts(x, f)) ? latest : null;
}

function distinct(list: (ShiftFacts | null)[]): ShiftFacts[] {
  const out: ShiftFacts[] = [];
  for (const f of list) if (f && !out.some((o) => sameFacts(o, f))) out.push(f);
  return out;
}

/**
 * The facts a volunteer's answer is filed against.
 *
 * An agreement is to the shift their page shows, and to nothing else — which is
 * what leaves an unpublished change asking again. A refusal is about the
 * person, so it repeats at the live shift where every coordinator surface reads
 * it. An agreement reaches the live shift only where that already stood agreed
 * (a coordinator's word by phone): written there unconditionally it would
 * overwrite that; left out, a refusal withdrawn on the page falls back to
 * waiting at the live shift instead of standing for good.
 */
export function shiftAnswerFacts(
  status: ShiftAnswerGiven,
  shown: ShiftFacts,
  live: ShiftFacts | null,
  liveAnswer: ShiftAnswerRow | null,
): ShiftFacts[] {
  if (status !== 'ok') return distinct([shown, live]);
  return distinct([shown, live && liveAnswer?.status === 'ok' ? live : null]);
}

/** A coordinator records an answer for the live shift and for what the page shows. */
export function coordinatorAnswerFacts(live: ShiftFacts, shown: ShiftFacts | null): ShiftFacts[] {
  return distinct([live, shown]);
}

/**
 * The person's confirmation made of their shift answers: refused if any shift
 * is refused or asked to change, confirmed once every shift is agreed, waiting
 * otherwise. `null` for a schedule with nothing to answer — the caller leaves
 * the person's status as it is. Shifts already done are the caller's to leave out.
 */
export function derivePersonStatus(shifts: readonly ShiftFacts[], rows: readonly ShiftAnswerRow[]): ConfirmationStatus | null {
  if (!shifts.length) return null;
  const answers = shifts.map((f) => answerFor(rows, f)?.status ?? null);
  if (answers.some((s) => s === 'change' || s === 'cant')) return 'zavrnjen';
  return answers.every((s) => s === 'ok') ? 'potrjen' : 'čaka';
}

/**
 * One shift read the way a person is read: agreed, refused or asked to change,
 * or still asked. What every screen colours a shift by — a shift with no
 * standing answer, or one whose answer was taken back, is waiting.
 */
export function shiftStanding(status: ShiftAnswerStatus | null | undefined): ConfirmationStatus {
  if (status === 'ok') return 'potrjen';
  return status === 'change' || status === 'cant' ? 'zavrnjen' : 'čaka';
}

/**
 * The word standing on each shift, out of the answers standing on them: what
 * the board and the roster carry for every login (`shiftStatus`), and what a
 * client reads out of `shiftAnswers` where a payload carries none.
 */
export function standingWordsOf<A extends { status: ShiftAnswerStatus }>(
  answers: Record<string, A> | undefined,
): Record<string, A['status']> {
  return Object.fromEntries(Object.entries(answers ?? {}).map(([shiftId, a]) => [shiftId, a.status]));
}

/** "ne more: GM3 · Pet 11:45–16:30" — a refused shift named the way every screen names it. */
export function refusalText(r: {
  status: ShiftAnswerStatus;
  postCode: string;
  dayLabel: string;
  fromMin: number;
  toMin: number;
}): string {
  const verb = r.status === 'change' ? 'predlaga spremembo' : 'ne more';
  return `${verb}: ${r.postCode} · ${r.dayLabel.slice(0, 3)} ${minutesToHm(r.fromMin)}–${minutesToHm(r.toMin)}`;
}

/** A shift can no longer be answered from its first minute, in Europe/Ljubljana. */
export function shiftStarted(s: { date: string; fromMin: number }, now: { isoDate: string; minutes: number }): boolean {
  return s.date < now.isoDate || (s.date === now.isoDate && s.fromMin <= now.minutes);
}

function nextDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * The other half of a shift that runs past midnight. The import splits one into
 * `[od, 24:00]` on its day and `[00:00, do]` at the same post on the next
 * (IMPORT-2026.md); to the person it is one shift, so it is answered as one.
 */
export function splitPartner<S extends { id: string; postId: string; date: string; fromMin: number; toMin: number }>(
  shifts: readonly S[],
  s: S,
): S | null {
  if (s.toMin === 1440) {
    const next = nextDate(s.date);
    return shifts.find((p) => p.id !== s.id && p.postId === s.postId && p.date === next && p.fromMin === 0) ?? null;
  }
  if (s.fromMin === 0) {
    return shifts.find((p) => p.id !== s.id && p.postId === s.postId && nextDate(p.date) === s.date && p.toMin === 1440) ?? null;
  }
  return null;
}
